/**
 * InvoiceManagement — FM3 Invoices + Installments + Payments + Receipts
 *
 * Route: /finance/invoices
 * RBAC:  finance.invoice.view.any   (all staff finance roles)
 *        finance.invoice.create     (finance_officer, finance_manager, institution_admin, super_admin)
 *        finance.payment.record     (finance_manager, finance_officer, institution_admin, super_admin)
 *        finance.payment.reconcile  (finance_manager, institution_admin, super_admin)
 *        finance.charge.adjust      (finance_manager, institution_admin, super_admin)
 *
 * Tabs:
 *   Eligible Assessments → select → generate invoice
 *   Invoices             → list → issue/cancel → installment plan → detail
 *   Payments             → record → allocate → post → receipt
 */
import React, { useEffect, useState, useCallback } from "react";
import {
  FileText, CreditCard, CheckCircle2, XCircle, Clock, AlertTriangle,
  RefreshCw, ChevronRight, ReceiptText, Plus, ArrowRight, Eye,
  Banknote, CalendarDays, Users, Hash, Shield, Info
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/layout/Shell";
import { ResponsivePageContainer } from "../components/shared/ResponsivePageContainer";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from "../components/ui/dialog";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle
} from "../components/ui/sheet";
import {
  Alert, AlertDescription, AlertTitle
} from "../components/ui/alert";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from "../components/ui/table";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";

// ─── Constants & Helpers ──────────────────────────────────────────────────────

const CAN_CREATE = ["institution_admin", "super_admin", "finance_officer", "finance_manager"];
const CAN_MANAGE = ["institution_admin", "super_admin"];
const CAN_RECORD_PAYMENT = ["institution_admin", "super_admin", "finance_officer", "finance_manager"];
const CAN_POST_PAYMENT = ["institution_admin", "super_admin", "finance_manager"];
const CAN_ADJUST = ["institution_admin", "super_admin", "finance_manager"];

function fmt(amount) {
  if (amount == null) return "—";
  return parseFloat(amount).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function StatusBadge({ status }) {
  const map = {
    DRAFT:     "bg-slate-100 text-slate-700",
    ISSUED:    "bg-emerald-100 text-emerald-700",
    CANCELLED: "bg-red-100 text-red-700",
    PENDING:   "bg-amber-100 text-amber-700",
    POSTED:    "bg-green-100 text-green-700",
    ACTIVE:    "bg-blue-100 text-blue-700",
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${map[status] || "bg-slate-100 text-slate-600"}`}>
      {status}
    </span>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

// FM3A — Eligible Assessments panel
function EligibleAssessmentsPanel({ canCreate, onGenerated }) {
  const [loading, setLoading] = useState(false);
  const [assessments, setAssessments] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [dueDate, setDueDate] = useState("");
  const [generating, setGenerating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/finance/invoices/eligible-assessments");
      setAssessments(d.assessments || []);
    } catch (e) {
      toast.error("Failed to load eligible assessments");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleAll = () => {
    if (selected.size === assessments.length) setSelected(new Set());
    else setSelected(new Set(assessments.map(a => a.assessment_id)));
  };

  const toggleOne = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };

  const handleGenerate = async () => {
    if (selected.size === 0) { toast.warning("Select at least one assessment."); return; }
    if (!dueDate) { toast.warning("Enter a due date."); return; }
    setGenerating(true);
    try {
      const { data: result } = await api.post("/finance/invoices/generate", {
        assessment_ids: [...selected],
        due_date: dueDate,
        confirm: true,
      });
      toast.success(`Generated ${result.generated_count} invoice(s).`);
      setSelected(new Set());
      onGenerated();
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : (msg?.message || "Invoice generation failed."));
    } finally {
      setGenerating(false);
    }
  };

  // Group by student for display
  const byStudent = assessments.reduce((acc, a) => {
    const k = a.student_number;
    if (!acc[k]) acc[k] = { student_number: a.student_number, student_name: a.student_name, items: [] };
    acc[k].items.push(a);
    return acc;
  }, {});

  if (loading) return (
    <div className="flex items-center gap-2 py-12 justify-center text-slate-500">
      <RefreshCw className="w-4 h-4 animate-spin" /> Loading eligible assessments…
    </div>
  );

  if (assessments.length === 0) return (
    <div className="py-16 text-center">
      <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
      <p className="text-slate-600 font-medium">No pending assessments eligible for invoicing.</p>
      <p className="text-slate-400 text-sm mt-1">All authorized assessments have been invoiced.</p>
    </div>
  );

  return (
    <div className="space-y-4">
      <Alert className="border-amber-200 bg-amber-50">
        <Shield className="h-4 w-4 text-amber-600" />
        <AlertTitle className="text-amber-800 text-sm font-semibold">Governed Action</AlertTitle>
        <AlertDescription className="text-amber-700 text-sm">
          Invoice generation converts authorized FeeAssessments into formal student obligations.
          Invoices are created in <strong>DRAFT</strong> status and must be issued explicitly.
          Only PENDING assessments without existing charges are eligible.
        </AlertDescription>
      </Alert>

      {canCreate && (
        <div className="flex flex-wrap items-end gap-4 p-4 bg-slate-50 rounded-lg border">
          <div className="flex-1 min-w-[180px]">
            <Label className="text-xs text-slate-600 mb-1 block">Due Date *</Label>
            <Input
              type="date"
              value={dueDate}
              onChange={e => setDueDate(e.target.value)}
              className="h-9 text-sm"
              data-testid="invoice-due-date-input"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={toggleAll}
            data-testid="toggle-all-assessments-btn"
          >
            {selected.size === assessments.length ? "Deselect All" : `Select All (${assessments.length})`}
          </Button>
          <Button
            size="sm"
            onClick={handleGenerate}
            disabled={generating || selected.size === 0 || !dueDate}
            data-testid="generate-invoices-btn"
          >
            {generating ? <RefreshCw className="w-3 h-3 animate-spin mr-1" /> : <Plus className="w-3 h-3 mr-1" />}
            Generate {selected.size > 0 ? `${selected.size} Invoice(s)` : "Invoices"}
          </Button>
        </div>
      )}

      <div className="rounded-lg border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              {canCreate && <TableHead className="w-10"></TableHead>}
              <TableHead>Student</TableHead>
              <TableHead>Rule</TableHead>
              <TableHead>Fee Type</TableHead>
              <TableHead>Schedule</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Assessment Date</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {Object.values(byStudent).map(group =>
              group.items.map((a, idx) => (
                <TableRow key={a.assessment_id} className="hover:bg-slate-50/50" data-testid={`assessment-row-${a.assessment_id}`}>
                  {canCreate && (
                    <TableCell>
                      <Checkbox
                        checked={selected.has(a.assessment_id)}
                        onCheckedChange={() => toggleOne(a.assessment_id)}
                        data-testid={`assessment-check-${a.assessment_id}`}
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    {idx === 0 && (
                      <div>
                        <div className="font-medium text-sm">{a.student_number}</div>
                        <div className="text-xs text-slate-500">{a.student_name}</div>
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-sm font-mono text-slate-700">{a.rule_code}</TableCell>
                  <TableCell><StatusBadge status={a.fee_type} /></TableCell>
                  <TableCell className="text-xs text-slate-500">{a.schedule_code}</TableCell>
                  <TableCell className="text-right font-semibold text-sm">
                    {a.currency_iso} {fmt(a.assessed_amount)}
                  </TableCell>
                  <TableCell className="text-xs text-slate-500">{a.assessment_date}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// FM3A — Invoices panel
function InvoicesPanel({ canManage, canAdjust }) {
  const [loading, setLoading] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [showPlanModal, setShowPlanModal] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter !== "ALL" ? `?status=${statusFilter}` : "";
      const { data: d } = await api.get(`/finance/invoices${params}`);
      setInvoices(d.invoices || []);
      setTotal(d.total || 0);
    } catch (e) {
      toast.error("Failed to load invoices");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (inv) => {
    setDetailLoading(true);
    setSelectedInvoice(null);
    try {
      const { data: d } = await api.get(`/finance/invoices/${inv.id}`);
      setSelectedInvoice(d);
    } catch (e) {
      toast.error("Failed to load invoice detail");
    } finally {
      setDetailLoading(false);
    }
  };

  const handleIssue = async (invId) => {
    setActionLoading(true);
    try {
      await api.patch(`/finance/invoices/${invId}/issue`);
      toast.success("Invoice issued successfully.");
      load();
      if (selectedInvoice?.id === invId) {
        const { data: d } = await api.get(`/finance/invoices/${invId}`);
        setSelectedInvoice(d);
      }
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : (msg?.message || "Issue failed."));
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async (invId) => {
    if (!window.confirm("Cancel this invoice? The underlying assessments will revert to PENDING.")) return;
    setActionLoading(true);
    try {
      await api.patch(`/finance/invoices/${invId}/cancel`);
      toast.success("Invoice cancelled.");
      load();
      setSelectedInvoice(null);
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : (msg?.message || "Cancel failed."));
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40 h-8 text-sm" data-testid="invoice-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All statuses</SelectItem>
            <SelectItem value="DRAFT">Draft</SelectItem>
            <SelectItem value="ISSUED">Issued</SelectItem>
            <SelectItem value="CANCELLED">Cancelled</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-xs text-slate-500">{total} invoice(s)</span>
        <Button variant="ghost" size="icon" onClick={load} className="h-8 w-8" data-testid="refresh-invoices-btn">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 py-12 justify-center text-slate-500">
          <RefreshCw className="w-4 h-4 animate-spin" /> Loading invoices…
        </div>
      ) : invoices.length === 0 ? (
        <div className="py-16 text-center text-slate-400">
          <FileText className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          <p className="text-sm">No invoices found. Generate invoices from eligible assessments.</p>
        </div>
      ) : (
        <div className="rounded-lg border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Invoice #</TableHead>
                <TableHead>Student</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Due</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoices.map(inv => (
                <TableRow key={inv.id} className="hover:bg-slate-50/50 cursor-pointer" data-testid={`invoice-row-${inv.id}`}>
                  <TableCell className="font-mono text-sm font-medium">{inv.invoice_number}</TableCell>
                  <TableCell>
                    <div className="text-sm">{inv.student_number}</div>
                    <div className="text-xs text-slate-500">{inv.student_name}</div>
                  </TableCell>
                  <TableCell className="text-xs">{inv.invoice_date}</TableCell>
                  <TableCell className="text-xs">{inv.due_date}</TableCell>
                  <TableCell><StatusBadge status={inv.status} /></TableCell>
                  <TableCell className="text-right font-semibold text-sm">
                    {inv.currency_iso} {fmt(inv.total_amount)}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost" size="icon" className="h-7 w-7"
                      onClick={() => openDetail(inv)}
                      data-testid={`invoice-detail-btn-${inv.id}`}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Detail Sheet */}
      <Sheet open={!!selectedInvoice || detailLoading} onOpenChange={v => { if (!v) setSelectedInvoice(null); }}>
        <SheetContent side="right" className="w-full sm:w-[560px] overflow-y-auto">
          <SheetHeader>
            <SheetTitle data-testid="invoice-detail-title">
              {detailLoading ? "Loading…" : `Invoice ${selectedInvoice?.invoice_number || ""}`}
            </SheetTitle>
          </SheetHeader>

          {selectedInvoice && (
            <div className="mt-4 space-y-5">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-slate-500">Status</span><div className="mt-0.5"><StatusBadge status={selectedInvoice.status} /></div></div>
                <div><span className="text-slate-500">Student</span><div className="font-medium">{selectedInvoice.student_number}</div></div>
                <div><span className="text-slate-500">Invoice Date</span><div>{selectedInvoice.invoice_date}</div></div>
                <div><span className="text-slate-500">Due Date</span><div>{selectedInvoice.due_date}</div></div>
                <div><span className="text-slate-500">Currency</span><div>{selectedInvoice.currency_iso}</div></div>
                <div><span className="text-slate-500">Total</span><div className="font-bold text-base">{selectedInvoice.currency_iso} {fmt(selectedInvoice.total_amount)}</div></div>
              </div>

              <Separator />

              <div>
                <h4 className="text-sm font-semibold text-slate-700 mb-2">Line Items</h4>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50">
                      <TableHead>Description</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(selectedInvoice.lines || []).map(l => (
                      <TableRow key={l.id} data-testid={`invoice-line-${l.id}`}>
                        <TableCell className="text-sm">{l.description}</TableCell>
                        <TableCell className="text-right text-sm font-medium">{fmt(l.amount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {selectedInvoice.installment_plan && (
                <>
                  <Separator />
                  <div>
                    <h4 className="text-sm font-semibold text-slate-700 mb-2">Installment Plan</h4>
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead>#</TableHead>
                          <TableHead>Due</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedInvoice.installment_plan.lines.map(l => (
                          <TableRow key={l.id} data-testid={`installment-line-${l.id}`}>
                            <TableCell className="text-xs">{l.installment_number}</TableCell>
                            <TableCell className="text-xs">{l.due_date}</TableCell>
                            <TableCell className="text-right text-sm">{fmt(l.amount)}</TableCell>
                            <TableCell><StatusBadge status={l.status} /></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {canAdjust && selectedInvoice.installment_plan.status === "ACTIVE" && (
                      <Button
                        variant="outline" size="sm" className="mt-2 text-red-600 border-red-200"
                        onClick={async () => {
                          try {
                            await api.delete(`/finance/invoices/${selectedInvoice.id}/installment-plan`);
                            toast.success("Installment plan cancelled.");
                            const { data: d } = await api.get(`/finance/invoices/${selectedInvoice.id}`);
                            setSelectedInvoice(d);
                          } catch (e) { toast.error("Failed to cancel plan."); }
                        }}
                        data-testid="cancel-installment-plan-btn"
                      >
                        Cancel Plan
                      </Button>
                    )}
                  </div>
                </>
              )}

              {canAdjust && !selectedInvoice.installment_plan && selectedInvoice.status !== "CANCELLED" && (
                <Button
                  variant="outline" size="sm" className="w-full"
                  onClick={() => setShowPlanModal(true)}
                  data-testid="create-installment-plan-btn"
                >
                  <CalendarDays className="w-4 h-4 mr-1.5" /> Add Installment Plan
                </Button>
              )}

              <Separator />

              {canManage && (
                <div className="flex gap-2">
                  {selectedInvoice.status === "DRAFT" && (
                    <Button
                      size="sm" className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                      onClick={() => handleIssue(selectedInvoice.id)}
                      disabled={actionLoading}
                      data-testid="issue-invoice-btn"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-1" /> Issue Invoice
                    </Button>
                  )}
                  {selectedInvoice.status !== "CANCELLED" && (
                    <Button
                      variant="outline" size="sm" className="text-red-600 border-red-200"
                      onClick={() => handleCancel(selectedInvoice.id)}
                      disabled={actionLoading}
                      data-testid="cancel-invoice-btn"
                    >
                      <XCircle className="w-4 h-4 mr-1" /> Cancel
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Installment Plan Modal */}
      {selectedInvoice && (
        <InstallmentPlanModal
          open={showPlanModal}
          invoice={selectedInvoice}
          onClose={() => setShowPlanModal(false)}
          onCreated={async () => {
            setShowPlanModal(false);
            const { data: d } = await api.get(`/finance/invoices/${selectedInvoice.id}`);
            setSelectedInvoice(d);
            toast.success("Installment plan created.");
          }}
        />
      )}
    </div>
  );
}

// FM3B — Installment Plan Modal
function InstallmentPlanModal({ open, invoice, onClose, onCreated }) {
  const invoiceTotal = parseFloat(invoice?.total_amount || "0");
  const [lines, setLines] = useState([
    { due_date: "", amount: "" },
    { due_date: "", amount: "" },
  ]);
  const [planType, setPlanType] = useState("CUSTOM");
  const [saving, setSaving] = useState(false);

  const addLine = () => setLines([...lines, { due_date: "", amount: "" }]);
  const removeLine = (i) => {
    if (lines.length <= 2) return;
    setLines(lines.filter((_, idx) => idx !== i));
  };
  const updateLine = (i, field, val) => {
    const newLines = [...lines];
    newLines[i] = { ...newLines[i], [field]: val };
    setLines(newLines);
  };

  const linesTotal = lines.reduce((s, l) => s + (parseFloat(l.amount) || 0), 0);
  const diff = Math.abs(linesTotal - invoiceTotal);

  const handleSave = async () => {
    for (const l of lines) {
      if (!l.due_date || !l.amount) { toast.warning("Fill all installment fields."); return; }
      if (parseFloat(l.amount) <= 0) { toast.warning("Amounts must be > 0."); return; }
    }
    if (diff > 0.01) { toast.warning(`Installment total (${linesTotal.toFixed(2)}) must equal invoice total (${invoiceTotal.toFixed(2)}).`); return; }

    setSaving(true);
    try {
      await api.post(`/finance/invoices/${invoice.id}/installment-plan`, {
        plan_type: planType,
        installments: lines.map(l => ({ due_date: l.due_date, amount: parseFloat(l.amount) })),
      });
      onCreated();
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : (msg?.message || "Failed to create plan."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Create Installment Plan</DialogTitle>
          <DialogDescription>
            Invoice total: <strong>{invoice?.currency_iso} {fmt(invoice?.total_amount)}</strong>.
            Sum of installments must equal this amount.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-2">
          {lines.map((l, i) => (
            <div key={i} className="flex gap-2 items-end">
              <div className="flex-1">
                <Label className="text-xs">Due Date {i + 1}</Label>
                <Input
                  type="date" value={l.due_date}
                  onChange={e => updateLine(i, "due_date", e.target.value)}
                  className="h-8 text-sm mt-0.5"
                  data-testid={`installment-due-date-${i}`}
                />
              </div>
              <div className="flex-1">
                <Label className="text-xs">Amount {i + 1}</Label>
                <Input
                  type="number" step="0.01" value={l.amount}
                  onChange={e => updateLine(i, "amount", e.target.value)}
                  className="h-8 text-sm mt-0.5"
                  data-testid={`installment-amount-${i}`}
                />
              </div>
              {lines.length > 2 && (
                <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500"
                  onClick={() => removeLine(i)}>
                  <XCircle className="w-4 h-4" />
                </Button>
              )}
            </div>
          ))}

          <div className="flex items-center justify-between text-sm">
            <span className={`font-medium ${diff > 0.01 ? "text-red-600" : "text-emerald-600"}`}>
              Total: {linesTotal.toFixed(2)} / {invoiceTotal.toFixed(2)}
            </span>
            <Button variant="outline" size="sm" onClick={addLine} data-testid="add-installment-line-btn">
              <Plus className="w-3 h-3 mr-1" /> Add
            </Button>
          </div>

          <div className="flex gap-2 pt-2">
            <Button className="flex-1" onClick={handleSave} disabled={saving || diff > 0.01} data-testid="save-installment-plan-btn">
              {saving ? <RefreshCw className="w-3 h-3 animate-spin mr-1" /> : null}
              Create Plan
            </Button>
            <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// FM3C + FM3D + FM3E — Payments panel
function PaymentsPanel({ canRecord, canPost }) {
  const [loading, setLoading] = useState(false);
  const [payments, setPayments] = useState([]);
  const [total, setTotal] = useState(0);
  const [showRecordModal, setShowRecordModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [allocations, setAllocations] = useState([]);
  const [receipt, setReceipt] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/finance/payments");
      setPayments(d.payments || []);
      setTotal(d.total || 0);
    } catch (e) {
      toast.error("Failed to load payments");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openPaymentDetail = async (pay) => {
    setDetailLoading(true);
    setSelectedPayment(null);
    setAllocations([]);
    setReceipt(null);
    try {
      const [{ data: detail }, { data: alloc }] = await Promise.all([
        api.get(`/finance/payments/${pay.id}`),
        api.get(`/finance/payments/${pay.id}/allocations`),
      ]);
      setSelectedPayment(detail);
      setAllocations(alloc.allocations || []);
      // Try to get receipt
      if (pay.status === "POSTED") {
        try {
          const { data: r } = await api.get(`/finance/payments/${pay.id}/receipt`);
          setReceipt(r);
        } catch {}
      }
    } catch (e) {
      toast.error("Failed to load payment detail");
    } finally {
      setDetailLoading(false);
    }
  };

  const handlePost = async (payId) => {
    try {
      const { data: r } = await api.patch(`/finance/payments/${payId}/post`);
      toast.success(`Payment posted. Receipt: ${r.receipt_number}`);
      load();
      const { data: detail } = await api.get(`/finance/payments/${payId}`);
      setSelectedPayment(detail);
      try {
        const { data: rct } = await api.get(`/finance/payments/${payId}/receipt`);
        setReceipt(rct);
      } catch {}
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : (msg?.message || "Post failed."));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <span className="text-xs text-slate-500">{total} payment(s)</span>
        {canRecord && (
          <Button size="sm" onClick={() => setShowRecordModal(true)} data-testid="record-payment-btn">
            <Plus className="w-3 h-3 mr-1" /> Record Payment
          </Button>
        )}
        <Button variant="ghost" size="icon" onClick={load} className="h-8 w-8" data-testid="refresh-payments-btn">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 py-12 justify-center text-slate-500">
          <RefreshCw className="w-4 h-4 animate-spin" /> Loading payments…
        </div>
      ) : payments.length === 0 ? (
        <div className="py-16 text-center text-slate-400">
          <CreditCard className="w-10 h-10 mx-auto mb-3 text-slate-300" />
          <p className="text-sm">No payments recorded yet.</p>
        </div>
      ) : (
        <div className="rounded-lg border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Student</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Method</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead>Reference</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {payments.map(p => (
                <TableRow key={p.id} className="hover:bg-slate-50/50" data-testid={`payment-row-${p.id}`}>
                  <TableCell>
                    <div className="text-sm">{p.student_number}</div>
                    <div className="text-xs text-slate-500">{p.student_name}</div>
                  </TableCell>
                  <TableCell className="text-xs">{p.payment_date}</TableCell>
                  <TableCell className="text-xs">{p.payment_method}</TableCell>
                  <TableCell><StatusBadge status={p.status} /></TableCell>
                  <TableCell className="text-right font-semibold text-sm">
                    {p.currency_iso} {fmt(p.payment_amount)}
                  </TableCell>
                  <TableCell className="text-xs font-mono text-slate-500">{p.reference_number || "—"}</TableCell>
                  <TableCell>
                    <Button
                      variant="ghost" size="icon" className="h-7 w-7"
                      onClick={() => openPaymentDetail(p)}
                      data-testid={`payment-detail-btn-${p.id}`}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Payment Detail Sheet */}
      <Sheet
        open={!!selectedPayment || detailLoading}
        onOpenChange={v => { if (!v) { setSelectedPayment(null); setReceipt(null); } }}
      >
        <SheetContent side="right" className="w-full sm:w-[540px] overflow-y-auto">
          <SheetHeader>
            <SheetTitle data-testid="payment-detail-title">
              {detailLoading ? "Loading…" : "Payment Detail"}
            </SheetTitle>
          </SheetHeader>

          {selectedPayment && (
            <div className="mt-4 space-y-5">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-slate-500">Status</span><div className="mt-0.5"><StatusBadge status={selectedPayment.status} /></div></div>
                <div><span className="text-slate-500">Student</span><div className="font-medium">{selectedPayment.student_number}</div></div>
                <div><span className="text-slate-500">Date</span><div>{selectedPayment.payment_date}</div></div>
                <div><span className="text-slate-500">Method</span><div>{selectedPayment.payment_method}</div></div>
                <div><span className="text-slate-500">Amount</span><div className="font-bold">{selectedPayment.currency_iso} {fmt(selectedPayment.payment_amount)}</div></div>
                <div><span className="text-slate-500">Allocated</span><div className="text-sm">{fmt(selectedPayment.allocated_amount)} / unallocated: {fmt(selectedPayment.unallocated_amount)}</div></div>
                {selectedPayment.reference_number && <div className="col-span-2"><span className="text-slate-500">Reference</span><div className="font-mono text-sm">{selectedPayment.reference_number}</div></div>}
              </div>

              {allocations.length > 0 && (
                <>
                  <Separator />
                  <div>
                    <h4 className="text-sm font-semibold mb-2">Allocations</h4>
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead>Charge</TableHead>
                          <TableHead className="text-right">Allocated</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {allocations.map(a => (
                          <TableRow key={a.id} data-testid={`allocation-row-${a.id}`}>
                            <TableCell className="text-xs">{a.charge_description}</TableCell>
                            <TableCell className="text-right text-sm">{fmt(a.allocated_amount)}</TableCell>
                            <TableCell><StatusBadge status={a.status} /></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </>
              )}

              {receipt && (
                <>
                  <Separator />
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3" data-testid="receipt-section">
                    <div className="flex items-center gap-1.5 text-emerald-700 text-sm font-semibold mb-1">
                      <ReceiptText className="w-4 h-4" /> Receipt
                    </div>
                    <div className="text-xs text-slate-600 grid grid-cols-2 gap-1.5 mt-2">
                      <span className="text-slate-500">Number</span><span className="font-mono font-medium">{receipt.receipt_number}</span>
                      <span className="text-slate-500">Issued At</span><span>{new Date(receipt.issued_at).toLocaleString()}</span>
                      <span className="text-slate-500">Delivery</span><span>{receipt.delivery_method} / {receipt.delivery_status}</span>
                    </div>
                  </div>
                </>
              )}

              {canPost && selectedPayment.status === "PENDING" && (
                <Button
                  size="sm" className="w-full bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => handlePost(selectedPayment.id)}
                  data-testid="post-payment-btn"
                >
                  <CheckCircle2 className="w-4 h-4 mr-1" /> Post Payment + Generate Receipt
                </Button>
              )}

              <Alert className="border-slate-200 bg-slate-50 text-xs">
                <Info className="h-3 w-3 text-slate-500" />
                <AlertDescription className="text-slate-500">
                  No external payment gateway configured. Payments are recorded manually with method and reference number.
                </AlertDescription>
              </Alert>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Record Payment Modal */}
      {showRecordModal && (
        <RecordPaymentModal
          open={showRecordModal}
          onClose={() => setShowRecordModal(false)}
          onRecorded={() => { setShowRecordModal(false); load(); }}
        />
      )}
    </div>
  );
}

// FM3C — Record Payment Modal
function RecordPaymentModal({ open, onClose, onRecorded }) {
  const [form, setForm] = useState({
    student_account_id: "",
    payment_date: new Date().toISOString().slice(0, 10),
    payment_method: "BANK_TRANSFER",
    payment_amount: "",
    currency_id: "",
    reference_number: "",
  });
  const [accounts, setAccounts] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [sa, cur] = await Promise.all([
          api.get("/finance/student-accounts").catch(() => ({ accounts: [] })),
          api.get("/finance/currencies").catch(() => ({ currencies: [] })),
        ]);
        setAccounts(sa.accounts || []);
        const curs = cur.currencies || [];
        setCurrencies(curs);
        // Pre-select if exactly one currency
        if (curs.length === 1) set("currency_id", curs[0].id);
      } catch {}
    })();
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.student_account_id || !form.payment_date || !form.payment_amount || !form.currency_id) {
      toast.warning("Fill all required fields."); return;
    }
    setSaving(true);
    try {
      await api.post("/finance/payments", {
        ...form,
        payment_amount: parseFloat(form.payment_amount),
      });
      toast.success("Payment recorded in PENDING status.");
      onRecorded();
    } catch (e) {
      const msg = e?.response?.data?.detail;
      toast.error(typeof msg === "string" ? msg : (msg?.message || "Record failed."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Record Payment</DialogTitle>
          <DialogDescription>
            Payments are recorded in PENDING status and must be posted after allocation.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-2">
          <div>
            <Label className="text-xs">Student Account *</Label>
            {accounts.length > 0 ? (
              <Select value={form.student_account_id} onValueChange={v => set("student_account_id", v)}>
                <SelectTrigger className="h-8 text-sm mt-0.5" data-testid="record-payment-student-account">
                  <SelectValue placeholder="Select student…" />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map(a => (
                    <SelectItem key={a.id} value={a.id}>{a.display_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input
                value={form.student_account_id}
                onChange={e => set("student_account_id", e.target.value)}
                placeholder="Student account UUID"
                className="h-8 text-sm mt-0.5"
                data-testid="record-payment-student-account"
              />
            )}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Payment Date *</Label>
              <Input type="date" value={form.payment_date} onChange={e => set("payment_date", e.target.value)} className="h-8 text-sm mt-0.5" data-testid="record-payment-date" />
            </div>
            <div>
              <Label className="text-xs">Method *</Label>
              <Select value={form.payment_method} onValueChange={v => set("payment_method", v)}>
                <SelectTrigger className="h-8 text-sm mt-0.5" data-testid="record-payment-method">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {["CASH","CARD","BANK_TRANSFER","CHEQUE","ONLINE","INTERNAL"].map(m => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Amount *</Label>
              <Input type="number" step="0.01" value={form.payment_amount} onChange={e => set("payment_amount", e.target.value)} className="h-8 text-sm mt-0.5" data-testid="record-payment-amount" />
            </div>
            <div>
              <Label className="text-xs">Currency *</Label>
              {currencies.length > 0 ? (
                <Select value={form.currency_id} onValueChange={v => set("currency_id", v)}>
                  <SelectTrigger className="h-8 text-sm mt-0.5" data-testid="record-payment-currency">
                    <SelectValue placeholder="Currency…" />
                  </SelectTrigger>
                  <SelectContent>
                    {currencies.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.iso_code} — {c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input value={form.currency_id} onChange={e => set("currency_id", e.target.value)} placeholder="Currency UUID" className="h-8 text-sm mt-0.5" data-testid="record-payment-currency" />
              )}
            </div>
          </div>
          <div>
            <Label className="text-xs">Reference Number</Label>
            <Input value={form.reference_number} onChange={e => set("reference_number", e.target.value)} placeholder="Bank/cheque reference" className="h-8 text-sm mt-0.5" data-testid="record-payment-reference" />
          </div>

          <Alert className="border-slate-200 bg-slate-50 py-2">
            <Info className="h-3 w-3 text-slate-500" />
            <AlertDescription className="text-xs text-slate-500">
              No payment gateway configured (NOT_CONFIGURED). Payment recorded manually.
            </AlertDescription>
          </Alert>

          <div className="flex gap-2 pt-1">
            <Button className="flex-1" onClick={handleSave} disabled={saving} data-testid="save-payment-btn">
              {saving ? <RefreshCw className="w-3 h-3 animate-spin mr-1" /> : null} Record
            </Button>
            <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────────

export default function InvoiceManagement() {
  const { user } = useAuth();
  const [tab, setTab] = useState("assessments");
  const [invoiceRefreshKey, setInvoiceRefreshKey] = useState(0);

  const role = user?.role || "";
  const canView = ["super_admin","institution_admin","finance_manager","finance_officer","registrar","cfo"].includes(role);
  const canCreate = CAN_CREATE.includes(role);
  const canManage = CAN_MANAGE.includes(role);
  const canRecord = CAN_RECORD_PAYMENT.includes(role);
  const canPost = CAN_POST_PAYMENT.includes(role);
  const canAdjust = CAN_ADJUST.includes(role);

  if (!canView) {
    return (
      <ResponsivePageContainer>
        <div className="py-24 text-center text-slate-500">
          <Shield className="w-8 h-8 mx-auto mb-3 text-slate-400" />
          <p>You do not have access to Invoice Management.</p>
        </div>
      </ResponsivePageContainer>
    );
  }

  return (
    <ResponsivePageContainer>
      <PageHeader
        title="Invoices & Payments"
        description="FM3 — Invoice generation, installment plans, payment recording, and receipts."
        data-testid="invoice-management-page-header"
      />

      <Tabs value={tab} onValueChange={setTab} className="mt-6" data-testid="invoice-tabs">
        <TabsList className="mb-4">
          <TabsTrigger value="assessments" data-testid="tab-eligible-assessments">
            <FileText className="w-3.5 h-3.5 mr-1.5" /> Eligible Assessments
          </TabsTrigger>
          <TabsTrigger value="invoices" data-testid="tab-invoices">
            <ReceiptText className="w-3.5 h-3.5 mr-1.5" /> Invoices
          </TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments">
            <CreditCard className="w-3.5 h-3.5 mr-1.5" /> Payments
          </TabsTrigger>
        </TabsList>

        <TabsContent value="assessments">
          <EligibleAssessmentsPanel
            canCreate={canCreate}
            onGenerated={() => {
              setInvoiceRefreshKey(k => k + 1);
              setTab("invoices");
            }}
          />
        </TabsContent>

        <TabsContent value="invoices">
          <InvoicesPanel
            key={invoiceRefreshKey}
            canManage={canManage}
            canAdjust={canAdjust}
          />
        </TabsContent>

        <TabsContent value="payments">
          <PaymentsPanel
            canRecord={canRecord}
            canPost={canPost}
          />
        </TabsContent>
      </Tabs>
    </ResponsivePageContainer>
  );
}
